<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("sales_return_list") }}</h4>
        </div>
      </div>

      <div v-if="can('sales.sales_return', 'W')" class="page-btn">
        <router-link to="/return/item" class="btn btn-added">
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t("new_sales_return") }}
        </router-link>
      </div>
    </div>

    <ThemeDataTable
      ref="salesReturnTable"
      table-id="SalesReturnTable"
      :fetch-data="fetchReturnsPage"
      :filters="tableFilters"
      default-order-dir="desc"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("return_voucher_no") }}</th>
            <th>{{ $t("return_voucher_date") }}</th>
            <th>{{ $t("sales_voucher_no") }}</th>
            <th class="no-sort">{{ $t("sales_voucher_date") }}</th>
            <th class="no-sort">{{ $t("item_name") }}</th>
            <th>{{ $t("customer_name") }}</th>
            <th>{{ $t("unit_price") }}</th>
            <th>{{ $t("return_qty") }}</th>
            <th class="no-sort">{{ $t("discount") }}</th>
            <th class="no-sort">{{ $t("vat") }}</th>
            <th class="no-sort">{{ $t("total_return_price") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ret in rows" :key="ret.id">
            <td>{{ ret.return_voucher }}</td>
            <td>{{ ret.return_voucher_date }}</td>
            <td>{{ ret.sales_voucher }}</td>
            <td>{{ ret.sales_voucher_date }}</td>
            <td>
              <div class="d-flex align-items-center">
                <span class="avatar avatar-md me-2">
                  <img
                    v-if="ret.image"
                    :src="$getImageUrl(ret.image)"
                    alt="Item Image"
                  />
                  <span
                    v-else
                    class="avatar-title rounded bg-light text-secondary d-flex align-items-center justify-content-center w-100 h-100"
                  >
                    <i class="ti ti-photo"></i>
                  </span>
                </span>
                <span>{{ ret.item_name }}</span>
              </div>
            </td>
            <td>{{ ret.customer_name || '—' }}</td>
            <td>{{ formatMoney(ret.unit_price) }}</td>
            <td>{{ ret.return_qty }}</td>
            <td>{{ formatMoney(ret.discount) }}</td>
            <td>{{ formatMoney(ret.vat) }}</td>
            <td>{{ formatMoney(ret.total_return_price) }}</td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
  </div>
</template>

<script>
import { can as canAccess } from "@/helpers/permission";
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';
import { truncateMoney2 } from '@/utils';

export default {
  components: {
    ThemeDataTable,
  },
  data() {
    return {
      filterOptions: {
        category: [],
        customer: [],
        warehouse: [],
      },
    };
  },
  computed: {
    tableFilters() {
      return [
        {
          key: 'category',
          label: this.$t('category'),
          options: this.filterOptions.category,
        },
        {
          key: 'customer',
          label: this.$t('customer_name'),
          options: this.filterOptions.customer,
        },
        {
          key: 'warehouse',
          label: this.$t('warehouse'),
          options: this.filterOptions.warehouse,
        },
      ];
    },
  },
  methods: {
    formatMoney(amount) {
      return truncateMoney2(amount);
    },

    async fetchFilterOptions() {
      try {
        const [categoriesRes, customersRes, warehousesRes] = await Promise.all([
          this.$axios.get(apiUrls.categories.get),
          this.$axios.get(apiUrls.customers.get),
          this.$axios.get(apiUrls.inventory_warehouse.get),
        ]);
        this.filterOptions.category = (categoriesRes?.data || [])
          .map((c) => c.node || c.name)
          .filter(Boolean);
        this.filterOptions.customer = (customersRes?.data || [])
          .map((c) => c.customer_name || c.name)
          .filter(Boolean);
        this.filterOptions.warehouse = (warehousesRes?.data || [])
          .map((w) => w.name)
          .filter(Boolean);
      } catch (error) {
        console.error('Error fetching sales return filter options:', error);
      }
    },

    async fetchReturnsPage({ draw, start, length, search, orderColumn, orderDir, filters }) {
      const params = {
        draw,
        start,
        length,
        'search[value]': search || '',
        'order[0][column]': orderColumn,
        'order[0][dir]': orderDir,
        ...filters,
      };

      const response = await this.$axios.get(apiUrls.return_item.get, { params });
      const payload = response.data || {};

      return {
        data: payload.data || [],
        recordsTotal: payload.recordsTotal || 0,
        recordsFiltered: payload.recordsFiltered || 0,
      };
    },

    can(code, requiredLevel) {
      return canAccess(code, requiredLevel);
    },
  },
  created() {
    this.fetchFilterOptions();
  },
};
</script>
